import { QueryClient, useIsMutating } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useEffect, useMemo } from "react";

import { useGetConversationInfinite as useGeneratedGetInfiniteMessages } from "@/api/generated";
import type {
  EventModel,
  OutputContentList,
  OutputImageDynamicPart,
  UserInputContentList,
} from "@/api/generated/models";
import type { ClientEventModel } from "@/api/model/Conversation";
import { AttachmentType } from "@/api/mutations/conversation";
import { useAuthStore, useUserSentEventStore } from "@/store";
import type { AskUserQuestionAnswer } from "@/store/askUserQuestions";
import {
  type UserSentEventModelOutput,
  spliceClientIdFromUserSentEvents,
} from "@/utils/messages";

const TEMP_ID_ASSISTANT_MESSAGE = "temp-id-assistant-message";

let tempIdCounter = 0;
const nextTempId = () => `${TEMP_ID_ASSISTANT_MESSAGE}-${++tempIdCounter}`;

const STRUCTURED_CONTENT_EVENT_TYPES = new Set([
  "output_content",
  "ask_user_questions",
  "pre_clinic_steps",
  "document_preview",
  "commentable_content",
  "zocdoc_booking",
  "medication_history_request",
  "pre_async_requirements",
]);

export const MESSAGES_PER_PAGE = 25;
export const infiniteMessagesQueryKey = (conversationId: string) => [
  "conversation",
  "infinite",
  conversationId,
  MESSAGES_PER_PAGE,
];

/**
 * Normalize a server {@link EventModel} into the camelCased {@link ClientEventModel} the UI
 * renders. User events splice their optimistic `clientId` off `userSentEventStack` so an in-flight
 * send reconciles with its server echo. Shared by the single-conversation and group read paths.
 */
export const mapEventToClientEvent = (
  message: EventModel,
  userSentEventStack: UserSentEventModelOutput[],
): ClientEventModel => {
  let clientId: string | undefined;
  const {
    content,
    id,
    role,
    user_id,
    conversation_id,
    feedback,
    feedback_comment,
    type,
    created_at,
    sender_photo_url,
    sender_full_name_with_title,
    clinic_request_id,
    origin_clinic_request_id,
  } = message;

  if (role === "user" && Array.isArray(content)) {
    clientId = spliceClientIdFromUserSentEvents(
      content as UserInputContentList,
      userSentEventStack,
    );
  }

  return {
    id,
    content,
    role: role ?? "assistant",
    userId: user_id,
    conversationId: conversation_id,
    feedback: feedback ?? 0,
    feedbackComment: feedback_comment ?? undefined,
    type,
    createdAt: created_at ?? "",
    senderPhotoUrl: sender_photo_url ?? undefined,
    senderFullNameWithTitle: sender_full_name_with_title ?? undefined,
    clinicRequestId: clinic_request_id ?? undefined,
    originClinicRequestId: origin_clinic_request_id ?? undefined,
    ...(role === "user" && {
      clientId,
    }),
  } as ClientEventModel;
};

/**
 * Group a chronological (oldest-first) message list into turns: a new turn starts on each user
 * message, so an assistant's follow-on events attach to the preceding turn.
 * e.g. [user, assistant, user, assistant, assistant] -> [[user, assistant], [user, assistant, assistant]]
 */
export const groupMessagesIntoTurns = (
  allMessages: ClientEventModel[],
): ClientEventModel[][] =>
  allMessages.reduce((acc, curr) => {
    if (curr.role === "user" || acc.length === 0) {
      acc.push([curr]);
    } else {
      acc[acc.length - 1].push(curr);
    }
    return acc;
  }, [] as ClientEventModel[][]);

export const useInfiniteMessages = (conversationId?: string) => {
  const { userSentEvents, setConversationId } = useUserSentEventStore();
  const { isAuthInitialized } = useAuthStore();
  const isCompleteOnboardingPending =
    useIsMutating({ mutationKey: ["completeOnboarding"] }) > 0;

  useEffect(() => {
    if (conversationId) {
      setConversationId(conversationId);
    }
  }, [conversationId, setConversationId]);

  return useGeneratedGetInfiniteMessages(
    conversationId!,
    { limit: MESSAGES_PER_PAGE },
    {
      query: {
        queryKey: infiniteMessagesQueryKey(conversationId!),
        initialPageParam: null,
        getNextPageParam: (lastPage) => lastPage.pagination?.next_cursor,
        retry: (_failCount, error) => (error as AxiosError).status !== 404,
        meta: { suppressErrorToast: true },
        enabled:
          Boolean(conversationId) &&
          isAuthInitialized &&
          !isCompleteOnboardingPending,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        refetchInterval: 1000 * 30,
        staleTime: 30000,
        select: (data) => {
          const lastPage = data.pages[data.pages.length - 1];
          const userSentEventStack = [...userSentEvents];

          return {
            ...data,
            allMessages: data.pages
              .flatMap((page) =>
                page.messages.map((message) =>
                  mapEventToClientEvent(message, userSentEventStack),
                ),
              )
              .reverse(),
            pagination: lastPage?.pagination,
          };
        },
      },
    },
  );
};

/**
 * Organizes the return value of useInfiniteMessages into turns of conversation
 * e.g. [user, assistant, user, assistant, assistant] -> [[user, assistant], [user, assistant, assistant]]
 */
export const useInfiniteTurns = (conversationId?: string) => {
  const result = useInfiniteMessages(conversationId);

  const allTurns = useMemo(() => {
    const allMessages = result.data?.allMessages;
    if (!allMessages) return undefined;

    return groupMessagesIntoTurns(allMessages);
  }, [result.data?.allMessages]);

  return {
    ...result,
    data: result.data ? { ...result.data, allTurns } : undefined,
  };
};

export const createUserSentMessage = (
  conversationId: string,
  userSentMessage: string,
): EventModel => ({
  content: [{ type: "input_text", text: userSentMessage }],
  conversation_id: conversationId,
  id: crypto.randomUUID(),
  role: "user",
  type: "user_input",
  user_id: "temp-user-id",
  feedback: 0,
});

type UseInfiniteMessagesReturn = ReturnType<typeof useInfiniteMessages>["data"];

type StreamingEventModel = EventModel & { concluded?: boolean };

export const updateInfiniteMessagesFromUser =
  (
    conversationId: string,
    userSentMessage: string,
    attachments?: AttachmentType[],
  ) =>
  (prev: UseInfiniteMessagesReturn) => {
    const content: UserInputContentList = [];

    if (attachments?.length) {
      attachments.forEach((attachment) => {
        if (attachment.isImage) {
          content.push({
            type: "input_image_with_url",
            url: attachment.presignedUrl || attachment.localUri,
            detail: "auto",
          });
        } else {
          content.push({
            type: "input_file_with_url",
            url: attachment.presignedUrl || attachment.localUri,
            filename: attachment.name ?? "attachment",
            content_type: attachment.mimeType ?? "application/octet-stream",
          });
        }
      });
    }

    if (userSentMessage) {
      content.push({ type: "input_text", text: userSentMessage });
    }

    const newMessage: EventModel = {
      content,
      conversation_id: conversationId,
      id: crypto.randomUUID(),
      role: "user",
      type: "user_input",
      user_id: "temp-user-id",
      feedback: 0,
    };

    if (!prev) {
      return {
        pages: [
          {
            conversation: { id: conversationId },
            messages: [newMessage],
            pagination: {
              limit: MESSAGES_PER_PAGE,
              next_cursor: null,
              has_more: false,
            },
          },
        ],
        pageParams: [null],
      };
    }

    const updatedPages = [...prev.pages];

    if (updatedPages.length > 0) {
      const firstPage = updatedPages[0];
      const firstPageMessages = firstPage.messages || [];

      updatedPages[0] = {
        ...firstPage,
        messages: [newMessage, ...firstPageMessages],
      };
    } else {
      updatedPages.push({
        // @ts-expect-error
        conversation: { id: conversationId },
        messages: [newMessage],
        pagination: {
          limit: MESSAGES_PER_PAGE,
          next_cursor: null,
          has_more: false,
        },
      });
    }

    return {
      ...prev,
      pages: updatedPages,
    };
  };

export const updateInfiniteMessagesFromQuestionAnswers =
  (
    conversationId: string,
    answers: AskUserQuestionAnswer[],
    attachments?: AttachmentType[],
  ) =>
  /* Destructure single submission of AskUserQuestions response to reflect each question & answer as an event in the stream */
  (prev: UseInfiniteMessagesReturn) => {
    const newEvents: EventModel[] = [];

    answers.forEach((answer) => {
      newEvents.push({
        content: answer.question,
        conversation_id: conversationId,
        id: crypto.randomUUID(),
        role: "assistant",
        type: "output_text",
        user_id: "temp-user-id",
      });

      const content: UserInputContentList = [];
      (answer.attachment_ids ?? []).forEach((attachmentId) => {
        const attachment = attachments?.find(
          (candidate) => candidate.attachmentId === attachmentId,
        );
        if (!attachment) return;
        if (attachment.isImage) {
          content.push({
            type: "input_image_with_url",
            url: attachment.presignedUrl || attachment.localUri,
            detail: "auto",
          });
        } else {
          content.push({
            type: "input_file_with_url",
            url: attachment.presignedUrl || attachment.localUri,
            filename: attachment.name ?? "attachment",
            content_type: attachment.mimeType ?? "application/octet-stream",
          });
        }
      });
      if (answer.answer) {
        content.push({ type: "input_text", text: answer.answer });
      }

      newEvents.push({
        content,
        conversation_id: conversationId,
        id: crypto.randomUUID(),
        role: "user",
        type: "user_input",
        user_id: "temp-user-id",
      });
    });

    // Page messages are stored newest-first, so prepend the chronological list reversed.
    const prepended = [...newEvents].reverse();

    if (!prev) {
      return {
        pages: [
          {
            conversation: { id: conversationId },
            messages: prepended,
            pagination: {
              limit: MESSAGES_PER_PAGE,
              next_cursor: null,
              has_more: false,
            },
          },
        ],
        pageParams: [null],
      };
    }

    const updatedPages = [...prev.pages];

    if (updatedPages.length > 0) {
      const firstPage = updatedPages[0];
      updatedPages[0] = {
        ...firstPage,
        messages: [...prepended, ...(firstPage.messages || [])],
      };
    } else {
      updatedPages.push({
        // @ts-expect-error
        conversation: { id: conversationId },
        messages: prepended,
        pagination: {
          limit: MESSAGES_PER_PAGE,
          next_cursor: null,
          has_more: false,
        },
      });
    }

    return {
      ...prev,
      pages: updatedPages,
    };
  };

const isInProgressStreamingMessage = (
  message: StreamingEventModel | undefined,
): message is StreamingEventModel =>
  !!message &&
  message.role === "assistant" &&
  !message.concluded &&
  !STRUCTURED_CONTENT_EVENT_TYPES.has(message.type ?? "");

export const updateInfiniteMessagesFromServer =
  (
    conversationId: string,
    serverSentMessageData: string,
    type:
      | "content_positive_delta"
      | "content_negative_delta"
      | "content_boundary",
  ) =>
  (prev: UseInfiniteMessagesReturn) => {
    if (!prev) return prev;

    const updatedPages = [...prev.pages];

    if (updatedPages.length > 0) {
      const firstPage = updatedPages[0];
      const firstPageMessages = [...(firstPage.messages || [])];
      const firstMessage = firstPageMessages[0] as
        | StreamingEventModel
        | undefined;

      if (isInProgressStreamingMessage(firstMessage)) {
        // Modify content of in-progress message
        if (typeof firstMessage.content !== "string") {
          return prev;
        }

        const updatedFirstMessage = { ...firstMessage };
        if (type === "content_boundary") {
          updatedFirstMessage.concluded = true;
        } else if (type === "content_positive_delta") {
          updatedFirstMessage.content =
            firstMessage.content + serverSentMessageData;
        } else {
          updatedFirstMessage.content = firstMessage.content.slice(
            0,
            -serverSentMessageData.length,
          );
        }
        firstPageMessages[0] = updatedFirstMessage;
        updatedPages[0] = {
          ...firstPage,
          messages: firstPageMessages,
        };
      } else if (firstMessage && type !== "content_boundary") {
        // Create a new message
        const newAssistantMessage: EventModel = {
          content: serverSentMessageData,
          conversation_id: conversationId,
          id: nextTempId(),
          role: "assistant",
          type: "output_text",
          user_id: "temp-user-id",
          feedback: 0,
        };

        firstPageMessages.unshift(newAssistantMessage);
        updatedPages[0] = {
          ...firstPage,
          messages: firstPageMessages,
        };
      }
    }

    return {
      ...prev,
      pages: updatedPages,
    };
  };

export const updateInfiniteMessagesWithStructuredOutput =
  (
    conversationId: string,
    content: OutputContentList,
    type: string,
    eventId?: string,
  ) =>
  (prev: UseInfiniteMessagesReturn) => {
    if (!prev) return prev;

    const updatedPages = [...prev.pages];

    if (updatedPages.length > 0) {
      const firstPage = updatedPages[0];
      const firstPageMessages = [...(firstPage.messages || [])];
      const newAssistantMessage: EventModel = {
        content,
        conversation_id: conversationId,
        id: eventId ?? nextTempId(),
        role: "assistant",
        type,
        user_id: "temp-user-id",
        feedback: 0,
      };

      firstPageMessages.unshift(newAssistantMessage);
      updatedPages[0] = {
        ...firstPage,
        messages: firstPageMessages,
      };
    }

    return {
      ...prev,
      pages: updatedPages,
    };
  };

export const updateUserMessageTranscription =
  (conversationId: string, transcription: string) =>
  (prev: UseInfiniteMessagesReturn) => {
    if (!prev) return prev;

    const updatedPages = [...prev.pages];

    if (updatedPages.length > 0) {
      const firstPage = updatedPages[0];
      const firstPageMessages = [...(firstPage.messages || [])];
      const firstMessage = firstPageMessages[0];

      // Update the first user message if it's the placeholder
      if (
        firstMessage &&
        firstMessage.role === "user" &&
        Array.isArray(firstMessage.content) &&
        firstMessage.content[0]?.type === "input_text" &&
        firstMessage.content[0]?.text === "[Voice message]"
      ) {
        firstPageMessages[0] = {
          ...firstMessage,
          content: [{ type: "input_text", text: transcription }],
        };
        updatedPages[0] = {
          ...firstPage,
          messages: firstPageMessages,
        };
      }
    }

    return {
      ...prev,
      pages: updatedPages,
    };
  };

export const updateInfiniteMessagesFromSocket =
  (newMessage: EventModel) => (prev: UseInfiniteMessagesReturn) => {
    if (!prev) return prev;

    let foundMessage = false;

    // Shallow clone the data, and update message in-place if found
    const newData = { ...prev };
    for (const page of newData.pages) {
      for (let i = 0; i < page.messages.length; i++) {
        const message = page.messages[i];
        if (message.id === newMessage.id) {
          foundMessage = true;
          page.messages[i] = newMessage;
        }
      }
    }

    if (!foundMessage) {
      // Otherwise, add to the beginning of the data (it'll get reversed)
      // We don't need to worry about chunking because this is the first page,
      // and the pagination cursor is unchanged
      newData.pages = [
        {
          ...newData.pages[0],
          messages: [newMessage, ...newData.pages[0].messages],
        },
        ...newData.pages.slice(1),
      ];
    }

    return newData;
  };

export const updateFieldsForEvent = (
  queryClient: QueryClient,
  conversationId: string,
  eventId: string,
  updatedFields: Partial<EventModel>,
) => {
  const queryKey = infiniteMessagesQueryKey(conversationId);
  queryClient.setQueryData(queryKey, (prev: UseInfiniteMessagesReturn) => {
    if (!prev) return prev;

    return {
      ...prev,
      pages: prev.pages.map((page) => ({
        ...page,
        messages: page.messages.map((event) => {
          if (event.id === eventId) {
            return { ...event, ...updatedFields };
          }
          return event;
        }),
      })),
    };
  });
};

export const updateCuraiConversationId = (
  noriConversationId: string,
  curaiConversationId: string,
  queryClient: QueryClient,
) => {
  const queryKey = infiniteMessagesQueryKey(noriConversationId);
  queryClient.setQueryData(queryKey, (prev: UseInfiniteMessagesReturn) => {
    if (!prev) return prev;

    return {
      ...prev,
      pages: prev.pages.map((page) => ({
        ...page,
        conversation: {
          ...page.conversation,
          curai_conversation_id: curaiConversationId,
        },
      })),
    };
  });
};

export const updateImageGenerationStatus = (
  queryClient: QueryClient,
  conversationId: string,
  eventId: string,
  images: Omit<OutputImageDynamicPart, "type">[],
) => {
  const imageMap = images.reduce(
    (acc, image) => {
      acc[image.image_id] = image;
      return acc;
    },
    {} as { [key: string]: Omit<OutputImageDynamicPart, "type"> },
  );

  let eventFound = false;

  const queryKey = infiniteMessagesQueryKey(conversationId);
  queryClient.setQueryData(queryKey, (prev: UseInfiniteMessagesReturn) => {
    if (!prev) return prev;

    return {
      ...prev,
      pages: prev.pages.map((page) => ({
        ...page,
        messages: page.messages.map((event) => {
          if (event.id === eventId && Array.isArray(event.content)) {
            eventFound = true;
            const updatedContent = event.content.map((part) => {
              if (part.type === "output_image_dynamic") {
                const update = imageMap[part.image_id];
                if (update) {
                  return {
                    ...part,
                    status: update.status,
                    url: update.url,
                  };
                }
              }
              return part;
            });

            return { ...event, content: updatedContent };
          }
          return event;
        }),
      })),
    };
  });
  // If we didn't find the event, we may still be using using temp-id-assistant-message, so invalidate + refetch
  if (!eventFound) {
    queryClient.invalidateQueries({ queryKey });
  }
};
